import axios from 'axios';
import store from '../store/store';

import {
  getLbApiEndpoint,
  getBetaEndpoint,
  getAWSEndpoint,
  getLbPropositionApiEndpoint,
  getLbUserApiEndpoint,
  getNotificationsEndpoint,
  getAuthenticationEndpoint,
  getPaymentEndpoint,
  getGrowthbookEndpoint,
  getAdmin2ApiEndpoint
} from './getEndpoint';
import { log } from './logger';
import { getCountry } from '../plugins/country/module';

const authenticationApi = axios.create({
  baseURL: getAuthenticationEndpoint(),
  withCredentials: true,
  headers: { country: getCountry() }
});

const paymentApi = axios.create({
  baseURL: getPaymentEndpoint(),
  withCredentials: true,
  'content-Type': 'application/json'
});
addAuthInterceptor(paymentApi);

const lbApi = axios.create({
  baseURL: getLbApiEndpoint(),
  withCredentials: true,
  'content-Type': 'application/json',
  credentials: 'same-origin',
  'cache-Control': 'no-cache'
});
addAuthInterceptor(lbApi);

const lbUserApi = axios.create({
  baseURL: getLbUserApiEndpoint(),
  withCredentials: true,
  'content-Type': 'application/json'
});

const lbPropositionApi = axios.create({
  baseURL: getLbPropositionApiEndpoint(),
  withCredentials: true,
  'content-Type': 'application/json'
});
addAuthInterceptor(lbPropositionApi);

const lbApiBeta = axios.create({
  baseURL: getBetaEndpoint(),
  withCredentials: true,
  'content-Type': 'application/json',
  credentials: 'same-origin'
});
addAuthInterceptor(lbApiBeta);

const lbAws = axios.create({
  baseURL: getAWSEndpoint(),
  withCredentials: true,
  'content-Type': 'application/json',
  credentials: 'same-origin'
});
addAuthInterceptor(lbAws);

const lbNotificationsApi = axios.create({
  baseURL: getNotificationsEndpoint(),
  withCredentials: true,
  'content-Type': 'application/json',
  credentials: 'same-origin'
});
addAuthInterceptor(lbNotificationsApi);

const growthbookApi = axios.create({
  baseURL: getGrowthbookEndpoint(),
  'content-Type': 'application/json',
  timeout: 5000
});

const lbAdmin2Api = axios.create({
  baseURL: getAdmin2ApiEndpoint(),
  withCredentials: true,
  'content-Type': 'application/json',
  credentials: 'same-origin'
});
addAuthInterceptor(lbAdmin2Api);

// A spurious 401 from any single microservice must not kill the whole
// session: auth/logout invalidates the JWT server-side, and the token is
// shared with the native app. Verify the 401 against /user/current
// (lbUserApi has no auth interceptor, so no recursion) before logging out,
// share one verification across concurrent 401s, and cool down after a
// confirmed logout so straggling 401s don't re-trigger the cascade.
const LOGOUT_COOLDOWN_MS = 10000;
let pendingUnauthorizedCheck = null;
let lastConfirmedLogoutAt = 0;

function addAuthInterceptor(instance) {
  instance.interceptors.response.use(
    response => response,
    async e => {
      if (e && e.response && e.response.status && e.response.status === 401) {
        await handleUnauthorized();
      }

      return Promise.reject(e);
    }
  );
}

function handleUnauthorized() {
  if (Date.now() - lastConfirmedLogoutAt < LOGOUT_COOLDOWN_MS) {
    return Promise.resolve();
  }

  if (!pendingUnauthorizedCheck) {
    pendingUnauthorizedCheck = verifySessionAndLogout().finally(() => {
      pendingUnauthorizedCheck = null;
    });
  }

  return pendingUnauthorizedCheck;
}

async function verifySessionAndLogout() {
  try {
    const response = await lbUserApi.get('/user/current');
    if (response.data && response.data.userId) {
      // The session is still valid — the 401 was spurious.
      return;
    }
  } catch (error) {
    if (!(error.response && error.response.status === 401)) {
      // Transient failure — we can't prove the session is dead, so don't
      // log the user out.
      return;
    }
  }

  lastConfirmedLogoutAt = Date.now();

  try {
    // Inside the app webview the token is shared with the native app;
    // auth/logout would invalidate it server-side and log the app out too.
    if (!store.getters['app/isAppRequest']) {
      await authenticationApi.post('auth/logout');
      localStorage.clear();
    }
    await store.dispatch('app/checkAuth', null, { root: true });
  } catch (error) {
    log(`Failed to logout: ${error}`, true);
  }
}

export {
  lbApi,
  lbApiBeta,
  lbUserApi,
  lbPropositionApi,
  lbAws,
  lbNotificationsApi,
  authenticationApi,
  paymentApi,
  growthbookApi,
  lbAdmin2Api
};
